import * as THREE from 'three';
import {
  AVATURN_ANIMATED_FACE_MORPH_NAMES,
  EMOTION_MESH_NAME
} from '../constants/emotionBlendshapes';

const { head: HEAD_MESH, avaturnStripped: AVATURN_STRIPPED_MORPH_MESH_NAMES } =
  EMOTION_MESH_NAME;

/** The body clip every Avaturn GLB ships. */
export const AVATURN_CLIP_NAME = 'avaturn_animation';

function parseMorphTrackKey(trackName) {
  const match = trackName.match(/\.morphTargetInfluences\[([^\]]+)\]$/);
  return match?.[1] ?? null;
}

function isMeshMorphTrack(trackName, meshName) {
  return trackName.startsWith(`${meshName}.morphTargetInfluences`);
}

function resolveMorphName(key, morphDictionary) {
  if (!key) return null;
  if (AVATURN_ANIMATED_FACE_MORPH_NAMES.includes(key)) return key;
  if (!morphDictionary) return null;

  for (const [name, index] of Object.entries(morphDictionary)) {
    if (String(index) === key) return name;
  }

  return null;
}

function isNumericMorphIndex(key) {
  return key !== null && /^\d+$/.test(key);
}

function isAvaturnAnimatedMorphTrackKey(key, morphDictionary) {
  const morphName = resolveMorphName(key, morphDictionary);
  return morphName
    ? AVATURN_ANIMATED_FACE_MORPH_NAMES.includes(morphName)
    : false;
}

function isBulkMorphInfluencesTrack(trackName, meshName) {
  return trackName === `${meshName}.morphTargetInfluences`;
}

function getMorphMeshName(trackName) {
  if (isMeshMorphTrack(trackName, HEAD_MESH)) return HEAD_MESH;

  return (
    AVATURN_STRIPPED_MORPH_MESH_NAMES.find((meshName) =>
      isMeshMorphTrack(trackName, meshName)
    ) ?? null
  );
}

function normalizeMorphDictionaries(morphDictionaries) {
  if (!morphDictionaries) return {};

  if (
    morphDictionaries[HEAD_MESH] ||
    AVATURN_STRIPPED_MORPH_MESH_NAMES.some(
      (meshName) => morphDictionaries[meshName]
    )
  ) {
    return morphDictionaries;
  }

  return { [HEAD_MESH]: morphDictionaries };
}

function getBlinkMorphIndices(morphDictionary) {
  if (!morphDictionary) return [];

  return [...new Set(
    AVATURN_ANIMATED_FACE_MORPH_NAMES.map(
      (name) => morphDictionary[name]
    ).filter((index) => index !== undefined)
  )];
}

/**
 * Avaturn exports one weights track for the whole Head_Mesh morph array.
 * Pull blink indices out into per-index tracks the mixer can run alongside emotion.
 */
export function extractBlinkTracksFromBulkMorphTrack(
  track,
  morphDictionary,
  meshName = HEAD_MESH
) {
  if (!track?.times?.length || !track.values?.length) return [];

  const blinkIndices = getBlinkMorphIndices(morphDictionary);
  if (!blinkIndices.length) return [];

  const morphCount = track.values.length / track.times.length;
  if (!Number.isInteger(morphCount) || morphCount <= 0) return [];

  return blinkIndices.map((blinkIndex) => {
    const values = track.times.map(
      (_, frameIndex) => track.values[frameIndex * morphCount + blinkIndex]
    );

    return new THREE.NumberKeyframeTrack(
      `${meshName}.morphTargetInfluences[${blinkIndex}]`,
      Array.from(track.times),
      values
    );
  });
}

function evaluateHeadMorphStrip(
  trackName,
  { meshName = HEAD_MESH, morphDictionary = null } = {}
) {
  if (isBulkMorphInfluencesTrack(trackName, meshName)) return true;
  if (!isMeshMorphTrack(trackName, meshName)) return false;

  const morphKey = parseMorphTrackKey(trackName);
  if (!morphKey) return false;

  if (!isNumericMorphIndex(morphKey)) {
    return !AVATURN_ANIMATED_FACE_MORPH_NAMES.includes(morphKey);
  }

  if (!morphDictionary) return false;

  return !isAvaturnAnimatedMorphTrackKey(morphKey, morphDictionary);
}

/**
 * True when a morph track should be removed from avaturn_animation.
 * Head_Mesh keeps blink morphs; stripped meshes (e.g. Teeth_Mesh) drop all morphs.
 */
export function shouldStripMorphTrack(trackName, morphDictionaries = null) {
  const dictionaries = normalizeMorphDictionaries(morphDictionaries);
  const meshName = getMorphMeshName(trackName);

  if (!meshName) return false;

  if (AVATURN_STRIPPED_MORPH_MESH_NAMES.includes(meshName)) {
    return (
      isBulkMorphInfluencesTrack(trackName, meshName) ||
      !!parseMorphTrackKey(trackName)
    );
  }

  if (meshName === HEAD_MESH) {
    return evaluateHeadMorphStrip(trackName, {
      meshName,
      morphDictionary: dictionaries[HEAD_MESH] ?? null
    });
  }

  return false;
}

/**
 * Strips emotion/lipsync-owned morph tracks from avaturn body clips.
 * Head_Mesh keeps blink/look morphs; accessory meshes lose all morph animation.
 */
export function stripMorphTracksFromClip(clip, morphDictionaries = null) {
  if (!clip?.tracks?.length) return clip;

  const dictionaries = normalizeMorphDictionaries(morphDictionaries);
  const blinkTracks = [];
  const filteredTracks = [];

  for (const track of clip.tracks) {
    if (isBulkMorphInfluencesTrack(track.name, HEAD_MESH)) {
      blinkTracks.push(
        ...extractBlinkTracksFromBulkMorphTrack(
          track,
          dictionaries[HEAD_MESH],
          HEAD_MESH
        )
      );
      continue;
    }

    if (shouldStripMorphTrack(track.name, dictionaries)) continue;

    filteredTracks.push(track);
  }

  const nextTracks = [...filteredTracks, ...blinkTracks];

  if (nextTracks.length === clip.tracks.length && blinkTracks.length === 0) {
    return clip;
  }

  return new THREE.AnimationClip(clip.name, clip.duration, nextTracks);
}

export function stripMorphTracksFromClips(clips, morphDictionaries = null) {
  return (clips ?? []).map((clip) =>
    stripMorphTracksFromClip(clip, morphDictionaries)
  );
}

export function countStrippedMorphTracksInClip(
  clip,
  morphDictionaries = null
) {
  if (!clip?.tracks?.length) return 0;

  return clip.tracks.filter((track) =>
    shouldStripMorphTrack(track.name, morphDictionaries)
  ).length;
}

export function countAvaturnMorphTracksInClip(
  clip,
  morphDictionaries = null,
  meshName = HEAD_MESH
) {
  if (!clip?.tracks?.length) return 0;

  return clip.tracks.filter((track) => {
    if (!isMeshMorphTrack(track.name, meshName)) return false;
    return !shouldStripMorphTrack(track.name, morphDictionaries);
  }).length;
}

/** Tracks a base pose is read from. Morph tracks never match. */
const BASE_POSE_TRACK_PATTERN = /^(.*).(quaternion|position|scale)$/;

const BASE_POSE_PROPERTY_STRIDE = {
  quaternion: 4,
  position: 3,
  scale: 3
};

/** The clip a base pose is read from: avaturn_animation, else the first one. */
export function findAvaturnClip(clips) {
  if (!clips?.length) return null;

  return (
    clips.find((clip) => clip?.name === AVATURN_CLIP_NAME) ?? clips[0] ?? null
  );
}

/**
 * Keyframe 0 of every bone track, as plain data.
 *
 * Avaturn GLBs rest in a T-pose and keep their arms-down stance in the first
 * frame of avaturn_animation, so an avatar whose clip is never played has to be
 * posed from this. Only frame 0 is read: most avatars store a single keyframe
 * per bone, but arjun's clip is a full body animation whose later frames are
 * mid-motion.
 *
 * Returns null when there is nothing to pose from, which is the caller's signal
 * to play the clip instead of leaving the avatar in its T-pose.
 */
export function getAvaturnBasePose(clips) {
  const clip = findAvaturnClip(clips);
  if (!clip?.tracks?.length) return null;

  const pose = { quaternion: {}, position: {}, scale: {} };
  let trackCount = 0;

  for (const track of clip.tracks) {
    if (!track?.name || !track.values?.length) continue;

    const match = BASE_POSE_TRACK_PATTERN.exec(track.name);
    if (!match) continue;

    const [, nodeName, property] = match;
    const stride = BASE_POSE_PROPERTY_STRIDE[property];
    if (track.values.length < stride) continue;

    pose[property][nodeName] = Array.from(track.values.slice(0, stride));
    trackCount += 1;
  }

  return trackCount ? pose : null;
}

/**
 * Writes a base pose onto the matching named objects under `root`, and returns
 * how many it posed. Idempotent, so React StrictMode's double invocation is
 * harmless.
 *
 * `root` must contain the bones. AvatarLoader mounts the skeleton with
 * `<primitive object={nodes.Hips} />`, and Object3D.add detaches Hips from the
 * GLTF scene, so a scene-rooted call would pose nothing.
 */
export function applyAvaturnBasePose(root, basePose) {
  if (!root || !basePose) return 0;

  let posed = 0;

  root.traverse((object) => {
    const { name } = object;
    if (!name) return;

    const quaternion = basePose.quaternion[name];
    const position = basePose.position[name];
    const scale = basePose.scale[name];
    if (!quaternion && !position && !scale) return;

    if (quaternion) object.quaternion.fromArray(quaternion);
    if (position) object.position.fromArray(position);
    if (scale) object.scale.fromArray(scale);
    posed += 1;
  });

  if (posed) root.updateMatrixWorld(true);

  return posed;
}
